import type { AxiosInstance } from 'axios';
import type {
  CreateCustomerBody,
  CreateCustomerResponse,
  Customer,
  CustomerFinancialSummary,
  CustomerNameExistsError,
  CustomerPhoneExistsError,
  DeleteCustomerResponse,
  GetCustomerFinancialSummaryResponse,
  GetCustomerResponse,
  ListCustomersResponse,
  UpdateCustomerBody,
  UpdateCustomerResponse,
} from '../types/customers';
import type {
  OrderDoc,
  PaginatedOrders,
} from '../redux/backoffice/ordersSlice';
import {
  getApiErrorCode,
  getApiErrorData,
  getApiErrorStatus,
} from '../utils/apiError';

/** Server accepts uppercase; None means "all statuses". */
export type CustomerOrdersStatus = 'COMPLETED' | 'PENDING' | 'PARTIAL';

type AuthShape = { accessToken?: string | null };

const withAuth = (auth: AuthShape) => ({
  headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
});

const qs = (locationId: string, extra: Record<string, string | number> = {}) =>
  new URLSearchParams({
    location_id: locationId,
    ...Object.fromEntries(
      Object.entries(extra).map(([k, v]) => [k, String(v)]),
    ),
  }).toString();

const clampLimit = (limit: number) =>
  Math.min(100, Math.max(1, Number(limit) || 50));
const clampPage = (page: number) => Math.max(1, Number(page) || 1);

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);

export const parseCustomersResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && Array.isArray(res.customers)) {
    return {
      success: true,
      customers: res.customers,
      total: Number(res.total ?? res.customers.length),
      page: Number(res.page ?? 1),
      limit: Number(res.limit ?? 50),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const customers = Array.isArray(d.customers)
      ? d.customers
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      customers,
      total: Number(d.total ?? d.totalDocs ?? customers.length),
      page: Number(d.page ?? d.currentPage ?? 1),
      limit: Number(d.limit ?? d.perPage ?? 50),
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseCustomerResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && res.customer) {
    return {
      success: true,
      customer: res.customer,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    return {
      success: true,
      customer: res.data.customer ?? res.data,
      message: String(res.message || ''),
    };
  }

  return null;
};

const listCustomersCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  page: number,
  limit: number,
  auth: AuthShape,
  q?: string,
) => {
  const searchQ = String(q || '').trim();
  return axiosinstance.get<ListCustomersResponse>(
    `/bp_get_customers_information?${qs(locationId, {
      page: clampPage(page),
      limit: clampLimit(limit),
      ...(searchQ ? { q: searchQ } : {}),
    })}`,
    withAuth(auth),
  );
};

const getCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get<GetCustomerResponse>(
    `/bp_get_customer_information/${customerId}?${qs(locationId)}`,
    withAuth(auth),
  );
};

const createCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  payload: CreateCustomerBody,
  auth: AuthShape,
) => {
  return axiosinstance.post<CreateCustomerResponse>(
    `/bp_add_customer_information?${qs(locationId)}`,
    {
      bp_customer_name: payload.bp_customer_name.trim(),
      ...(payload.bp_customer_phone_number?.trim()
        ? {
            bp_customer_phone_number: payload.bp_customer_phone_number.trim(),
          }
        : {}),
      ...(payload.bp_customer_email_address
        ? {
            bp_customer_email_address: payload.bp_customer_email_address.trim(),
          }
        : {}),
      ...(payload.allow_duplicate_name ? { allow_duplicate_name: true } : {}),
    },
    withAuth(auth),
  );
};

const updateCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  payload: UpdateCustomerBody,
  auth: AuthShape,
) => {
  const body: UpdateCustomerBody = {};
  if (typeof payload.bp_customer_name === 'string') {
    body.bp_customer_name = payload.bp_customer_name.trim();
  }
  if (typeof payload.bp_customer_phone_number === 'string') {
    body.bp_customer_phone_number = payload.bp_customer_phone_number.trim();
  }
  if (typeof payload.bp_customer_email_address === 'string') {
    body.bp_customer_email_address = payload.bp_customer_email_address.trim();
  }
  return axiosinstance.patch<UpdateCustomerResponse>(
    `/bp_update_customer_information/${customerId}?${qs(locationId)}`,
    body,
    withAuth(auth),
  );
};

const deleteCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  return axiosinstance.delete<DeleteCustomerResponse>(
    `/bp_delete_customer_information/${customerId}?${qs(locationId)}`,
    withAuth(auth),
  );
};

export const listCustomers = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  page: number,
  limit: number,
  auth: AuthShape,
  q?: string,
) => {
  try {
    return await listCustomersCore(
      axiosinstance,
      locationId,
      page,
      limit,
      auth,
      q,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  try {
    return await getCustomerCore(axiosinstance, locationId, customerId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  payload: CreateCustomerBody,
  auth: AuthShape,
) => {
  try {
    return await createCustomerCore(axiosinstance, locationId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * The saved customer a create was refused for: the server allows one customer
 * per phone at a location and answers 409 `CUSTOMER_PHONE_EXISTS` with that
 * customer. Null for any other error.
 */
export const getExistingCustomerFromCreateError = (
  error: unknown,
): Customer | null => {
  if (
    getApiErrorStatus(error) !== 409 ||
    getApiErrorCode(error) !== 'CUSTOMER_PHONE_EXISTS'
  ) {
    return null;
  }
  const customer = getApiErrorData<CustomerPhoneExistsError>(error)?.customer;
  return customer?._id ? customer : null;
};

/**
 * The customers a create without a phone was refused for: the server answers
 * 409 `CUSTOMER_NAME_EXISTS` with the customers at the location who already
 * have the name. Sending `allow_duplicate_name` saves a new one anyway. Null
 * for any other error.
 */
export const getSameNameCustomersFromCreateError = (
  error: unknown,
): Customer[] | null => {
  if (
    getApiErrorStatus(error) !== 409 ||
    getApiErrorCode(error) !== 'CUSTOMER_NAME_EXISTS'
  ) {
    return null;
  }
  const customers = getApiErrorData<CustomerNameExistsError>(error)?.customers;
  const listed = Array.isArray(customers)
    ? customers.filter(customer => customer?._id)
    : [];
  return listed.length > 0 ? listed : null;
};

export const updateCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  payload: UpdateCustomerBody,
  auth: AuthShape,
) => {
  try {
    return await updateCustomerCore(
      axiosinstance,
      locationId,
      customerId,
      payload,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

/** Normalize `/bp_owner_get_customer_orders_information/:customerId` into the same shape the
 * backoffice orders slice already understands (see BackOfficeCompletedOrders
 * `normalizePaginated`). Accepts either flat payloads or `{ data: {...} }`. */
export const parseCustomerOrdersResponse = (
  response: any,
): PaginatedOrders | null => {
  const res = response?.data;
  if (!res) return null;

  const payload = res?.data && typeof res.data === 'object' ? res.data : res;

  const docs = Array.isArray(payload?.docs)
    ? (payload.docs as OrderDoc[])
    : Array.isArray(payload?.data)
      ? (payload.data as OrderDoc[])
      : [];

  const limit = Number(payload?.limit ?? payload?.perPage ?? 50);
  const perPage = Number(payload?.perPage ?? payload?.limit ?? limit);
  const page = Number(payload?.page ?? payload?.currentPage ?? 1);
  const totalDocs = Number(payload?.totalDocs ?? payload?.total ?? docs.length);
  const totalPages = Number(
    payload?.totalPages ??
      Math.max(1, Math.ceil(totalDocs / Math.max(perPage, 1))),
  );
  const hasNextPage = Boolean(payload?.hasNextPage ?? page < totalPages);
  const hasPrevPage = Boolean(payload?.hasPrevPage ?? page > 1);
  const nextPage = Number(payload?.nextPage ?? (hasNextPage ? page + 1 : page));

  return {
    docs,
    hasNextPage,
    hasPrevPage,
    limit,
    nextPage,
    page,
    perPage,
    totalDocs,
    totalPages,
  };
};

const getCustomerOrdersCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  page: number,
  limit: number,
  status: CustomerOrdersStatus | null,
  auth: AuthShape,
) => {
  const query = qs(locationId, {
    page: clampPage(page),
    limit: clampLimit(limit),
    ...(status ? { status } : {}),
  });
  // Owner-scoped: returns the same OrderDoc shape as the bp_owner_get_*_orders
  // tab endpoints (fully populated bp_product_information), so docs can be
  // handed straight to the back office order editor. `bp_get_customer_orders`
  // still exists for older clients but projects product info down to a name.
  return axiosinstance.get(
    `/bp_owner_get_customer_orders_information/${encodeURIComponent(
      customerId,
    )}?${query}`,
    withAuth(auth),
  );
};

export const getCustomerOrders = async (
  axiosinstance: AxiosInstance,
  args: {
    locationId: string;
    customerId: string;
    page?: number;
    limit?: number;
    status?: CustomerOrdersStatus | null;
  },
  auth: AuthShape,
) => {
  try {
    return await getCustomerOrdersCore(
      axiosinstance,
      args.locationId,
      args.customerId,
      Number(args.page ?? 1),
      Number(args.limit ?? 50),
      args.status ?? null,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  try {
    return await deleteCustomerCore(
      axiosinstance,
      locationId,
      customerId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

const toNumber = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toDateString = (value: any): string | null => {
  const str = String(value ?? '').trim();
  return str ? str : null;
};

// A body only counts as a summary if it carries at least one of these.
const SUMMARY_KEYS = [
  'totalSpent',
  'outstandingBalance',
  'totalOrders',
  'ordersByStatus',
] as const;

/** Normalize `GET /bp_get_customer_financial_summary_information/:customerId`.
 * Accepts `{ success, summary }`, a flat payload, or `{ data: {...} }`.
 *
 * Returns `null` when the body reports a failure (`success` present but not
 * `true`) or doesn't carry a summary at all, so the screen keeps its "—"
 * placeholders instead of rendering a zeroed "All paid up" summary. */
export const parseCustomerFinancialSummaryResponse = (
  response: any,
): CustomerFinancialSummary | null => {
  const res = response?.data;
  if (!res || typeof res !== 'object') return null;
  if ('success' in res && res.success !== true) return null;

  const payload =
    res?.summary && typeof res.summary === 'object'
      ? res.summary
      : res?.data && typeof res.data === 'object'
        ? (res.data.summary ?? res.data)
        : res;
  if (!payload || typeof payload !== 'object') return null;
  if (!SUMMARY_KEYS.some(key => key in payload)) return null;

  const byStatus = payload.ordersByStatus ?? {};
  return {
    totalSpent: toNumber(payload.totalSpent),
    outstandingBalance: toNumber(payload.outstandingBalance),
    totalOrders: toNumber(payload.totalOrders),
    averageOrderValue: toNumber(payload.averageOrderValue),
    lastOrderDate: toDateString(payload.lastOrderDate),
    lastPaymentDate: toDateString(payload.lastPaymentDate),
    totalRefunded: toNumber(payload.totalRefunded),
    ordersByStatus: {
      completed: toNumber(byStatus.completed),
      partial: toNumber(byStatus.partial),
      pending: toNumber(byStatus.pending),
    },
    customerSince: toDateString(payload.customerSince),
  };
};

const getCustomerFinancialSummaryCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get<GetCustomerFinancialSummaryResponse>(
    `/bp_get_customer_financial_summary_information/${encodeURIComponent(
      customerId,
    )}?${qs(locationId)}`,
    withAuth(auth),
  );
};

export const getCustomerFinancialSummary = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  try {
    return await getCustomerFinancialSummaryCore(
      axiosinstance,
      locationId,
      customerId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};
